import React, { useEffect, useState } from 'react';
import objectPath from 'object-path';
import { DestinationLogo } from 'src/common/DestinationLogo';
import { Content } from 'src/common/interfaces/content.interface';
import { Nullable } from 'src/types/nullable.type';
import nodeIcons from 'src/utils/nodeIcons';
import { useFloating, useHover, useInteractions, offset, autoUpdate, Placement } from '@floating-ui/react';
import { animated, easings, useSpring, useSpringRef } from '@react-spring/web';
import { useUpdateEffect } from 'react-use';
import { FLOAT_ANIMATION_CONFIG } from 'src/document-editor/neue-bubble-menu-fixed';
import { DocumentViewerFactory } from 'src/document-editor/viewer.factory';
import { useImageAssetSrcSet } from 'src/utils/use-image-asset-src-set.hook';
import { ImageRenderer } from 'src/common/ImageRenderer';
import { autoPlacement, computePosition } from '@floating-ui/react-dom';
import classNames from 'classnames';
import { NeueOpenaiRegularIcon } from 'src/monet/icons';
import { NeueNodeIcons } from 'src/utils/neue-node-icons';

type Props = {
  content: Content;
  updateKey: number;
  onSelect: () => void;
};

const getContentName = (content: Content): string => {
  if (content.name) return content.name;
  if (content.embed) return content.embed.url;
  if (content.external_link) return content.external_link.title || '';
  if (content.file_asset) return content.file_asset.name;
  if (content.image_asset) return content.image_asset.name;
  if (content.video_asset) return content.video_asset.name;

  return '';
};

const HOVER_DELAY_DURATION = 400;

export const MediaItem = ({ content, updateKey, onSelect }: Props) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [contentNameCopy, setContentNameCopy] = useState<string>(getContentName(content));
  const [placement, setPlacement] = useState<Placement>('left');
  const [showPreview, setShowPreview] = useState(false);

  const { refs, context, strategy, x, y } = useFloating({
    open: showPreview,
    placement,
    onOpenChange: setShowPreview,
    whileElementsMounted: autoUpdate,
    middleware: [offset({ mainAxis: 24 })],
  });

  useUpdateEffect(() => {
    if (refs.domReference.current && refs.floating.current) {
      computePosition(refs.domReference.current, refs.floating.current, {
        middleware: [autoPlacement({ allowedPlacements: ['left', 'right'] })],
      }).then(({ placement }) => {
        setPlacement(placement);
      });
    }
  }, [updateKey, refs.domReference, refs.floating]);

  const hover = useHover(context, { delay: !showPreview ? HOVER_DELAY_DURATION : 0 });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const externalLinkUrl = content.external_link?.url;

  useEffect(() => {
    if (content.external_link || content.embed) {
      setContentNameCopy(
        showPreview ? content.external_link?.title || content.embed?.url || '' : getContentName(content)
      );
    }
  }, [content, externalLinkUrl, showPreview]);

  const renderLogo = (content: Content) => {
    const destination = content.embed?.destination;
    const contentType = content.content_type;
    const NodeIcon =
      objectPath.get(NeueNodeIcons, contentType, null) ||
      (objectPath.get(nodeIcons, [contentType, 'regular'], null) as Nullable<React.ElementType>);
    const isOpenAi = !!content?.image_asset?.dalle_params;
    const assetImportSource = content?.video_asset?.asset_import?.source;

    if (isOpenAi) {
      return <NeueOpenaiRegularIcon className='w-5 h-5 flex-shrink-0' />;
    }

    if (destination || externalLinkUrl || assetImportSource) {
      return (
        <DestinationLogo
          destination={destination}
          externalLinkUrl={externalLinkUrl}
          assetImportSource={assetImportSource}
          size='small'
          isInNeueLibrary />
      );
    } else {
      if (NodeIcon) {
        return <NodeIcon className='w-5 h-5 flex-shrink-0' />;
      }
    }

    return null;
  };

  const api = useSpringRef();
  const [styles] = useSpring(
    () => ({
      ref: api,
      from: { y: 8, opacity: 0 },
      to: { y: 0, opacity: 1 },
    }),
    []
  );

  const renderDocument = DocumentViewerFactory(content);

  const thumbnailSrcSet = useImageAssetSrcSet(content?.thumbnail || undefined, 'thumbnail_300x300');

  const contentPreviewIsStaticImage = !!content.thumbnail?.url;
  const contentHasPreviewThumbnail = contentPreviewIsStaticImage || !!content.document;

  useUpdateEffect(() => {
    if (contentHasPreviewThumbnail && showPreview) {
      api.start({
        config: FLOAT_ANIMATION_CONFIG,
        from: { y: 8, opacity: 0 },
        to: { y: 0, opacity: 1 },
      });
    } else {
      api.start({
        opacity: 0,
        config: { duration: 200, easing: easings.easeOutCubic },
      });
    }
  }, [showPreview]);

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className='relative px-2 py-[9px] hover:bg-neue-journey-fg-10 rounded transition-colors cursor-pointer truncate'
        onClick={onSelect}
      >
        <div className='flex items-center space-x-2'>
          {renderLogo(content)}
          <div className='text-neue-journey-medium truncate'>{contentNameCopy}</div>
        </div>
      </div>
      <>
        <animated.div
          ref={refs.setFloating}
          style={{ position: strategy, top: y ?? 0, left: x ?? 0, ...styles }}
          className={classNames(
            'relative max-h-[11.25rem] overflow-hidden max-w-[20rem] w-full border-4 rounded-lg border-neue-journey-bg-90',
            {
              'h-[11.25rem]': !imageLoaded,
              'pointer-events-none': !showPreview,
            }
          )}
          {...getFloatingProps()}
        >
          <div className='absolute inset-0 bg-white opacity-100 transition ease-in z-neue-preview-background-overlay' />
          {contentPreviewIsStaticImage ? (
            <ImageRenderer
              src={content.thumbnail!.url}
              srcSet={thumbnailSrcSet}
              className='rounded object-contain m-auto'
              loadingClassName='bg-white rounded-md flex items-center justify-center w-full h-full'
              showLoading
              showSpinnerOnLoading
              onImageLoad={() => {
                setImageLoaded(true);
              }}
            />
          ) : (
            <div className='relative'>
              <div className='absolute inset-0 bg-bedrock-black-overlay rounded-md opacity-100 transition ease-in z-40' />
              {renderDocument()}
            </div>
          )}
        </animated.div>
      </>
    </>
  );
};
